import { t } from "@lingui/macro";
import {
  CircleNotch,
  CheckCircle,
  WarningCircle,
  ArrowClockwise,
  Download,
  Brain,
  Sparkle,
  Lightning,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useRef, useMemo } from "react";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";

import type {
  OptimizeResumeBaseEventDto,
  OptimizedIterationDto,
  OptimizedDto,
  OptimizedAnalysisIterationDto,
} from "@reactive-resume/dto";

// Simplified stages
type OptimizationStage = "initializing" | "processing" | "completed" | "error";

// Add custom type for completed event
type CompletedEventDto = {
  type: "completed";
  data: Record<string, any>;
};

type ProgressProps = {
  events: (
    | OptimizedDto
    | OptimizeResumeBaseEventDto
    | OptimizedIterationDto
    | OptimizedAnalysisIterationDto
    | CompletedEventDto
  )[];
  status: string;
  onRestart?: () => void;
  onSave?: (text: string) => void;
  onClose?: () => void;
  useMockEvents?: boolean;
};

type ProgressState = {
  currentText: string;
  stage: OptimizationStage;
  error: string | null;
  isStreaming: boolean;
  progress: number;
};

export const Progress = ({
  events,
  status,
  onRestart,
  onSave,
  onClose,
  useMockEvents = false,
}: ProgressProps) => {
  const [state, setState] = useState<ProgressState>({
    currentText: "",
    stage: "initializing",
    error: null,
    isStreaming: false,
    progress: 0,
  });

  // Process events from props
  useEffect(() => {
    if (!events.length) return;

    try {
      const lastEvent = events[events.length - 1];

      if (!lastEvent.type) {
        setState((prev) => ({ ...prev, stage: "error", error: "Invalid event received" }));
        return;
      }

      // Calculate progress based on event types and stage
      const calculateProgress = (eventType: string, currentStage: OptimizationStage) => {
        switch (eventType) {
          case "optimized-chunk-iteration":
            return Math.min(30 + events.length * 2, 70);
          case "optimized-iteration":
            return 75;
          case "optimized-analysis-iteration":
            return 85;
          case "optimized":
          case "completed":
            return 100;
          default:
            return currentStage === "initializing" ? 10 : 30;
        }
      };

      switch (lastEvent.type) {
        case "optimized-chunk-iteration": {
          setState((prev) => ({
            ...prev,
            currentText: prev.currentText + (lastEvent.data.text ?? ""),
            stage: "processing",
            isStreaming: true,
            progress: calculateProgress(lastEvent.type, prev.stage),
          }));
          break;
        }
        case "optimized-iteration": {
          setState((prev) => ({
            ...prev,
            currentText: lastEvent.data.text ?? prev.currentText,
            stage: "processing",
            isStreaming: false,
            progress: calculateProgress(lastEvent.type, prev.stage),
          }));
          break;
        }
        case "optimized-analysis-iteration": {
          setState((prev) => ({
            ...prev,
            stage: "processing",
            isStreaming: false,
            progress: calculateProgress(lastEvent.type, prev.stage),
          }));
          break;
        }
        case "optimized": {
          setState((prev) => ({
            ...prev,
            stage: "completed",
            isStreaming: false,
            progress: 100,
          }));
          break;
        }
        case "completed": {
          setState((prev) => ({
            ...prev,
            stage: "completed",
            isStreaming: false,
            progress: 100,
          }));
          break;
        }
        default: {
          console.warn("Unknown event type:", (lastEvent as any).type);
        }
      }
    } catch (error) {
      console.error("Error processing events:", error);
      setState((prev) => ({
        ...prev,
        stage: "error",
        error: "Error occurred while optimizing resume",
        isStreaming: false,
      }));
    }
  }, [events]);

  // Generate status message based on current stage
  const statusMessage = useMemo(() => {
    switch (state.stage) {
      case "initializing":
        return t`Initializing AI optimization process...`;
      case "processing":
        return state.isStreaming ? t`Optimizing your resume...` : t`Analyzing optimized content...`;
      case "completed":
        return t`Resume optimization completed successfully`;
      case "error":
        return state.error || t`An error occurred during optimization`;
      default:
        return t`Processing...`;
    }
  }, [state.stage, state.isStreaming, state.error]);

  const isProcessing = state.stage !== "completed" && state.stage !== "error";

  // Enhanced loading particles animation
  const LoadingParticles = () => (
    <div className="absolute inset-0 overflow-hidden opacity-40">
      {Array.from({ length: 12 }).map((_, i) => (
        <motion.div
          key={i}
          className="bg-primary absolute size-2 rounded-full"
          initial={{
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
            scale: 0,
          }}
          animate={{
            x: Math.random() * window.innerWidth,
            y: Math.random() * window.innerHeight,
            scale: [0, 1, 0],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: i * 0.2,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );

  return (
    <div className="flex h-screen w-full max-w-none flex-col overflow-hidden">
      {/* Loading particles */}
      {state.stage === "initializing" && <LoadingParticles />}

      {/* Action buttons - positioned absolutely in top right */}
      <div className="absolute top-6 right-6 z-30">
        <div className="flex items-center gap-2">
          {state.stage === "error" && (
            <motion.button
              onClick={onRestart}
              className="bg-primary/90 text-primary-foreground hover:bg-primary/80 inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm backdrop-blur-sm transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <ArrowClockwise className="size-4" />
              {t`Try Again`}
            </motion.button>
          )}

          {state.stage === "completed" && onSave && (
            <motion.button
              onClick={() => onSave(state.currentText)}
              className="bg-primary/90 text-primary-foreground hover:bg-primary/80 inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm backdrop-blur-sm transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <Download className="size-4" />
              {t`Save Optimized Resume`}
            </motion.button>
          )}
        </div>
      </div>

      {/* Status message - centered on screen */}
      <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${state.stage}-${state.isStreaming}`}
            initial={{ opacity: 0, y: 15, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -15, scale: 0.95 }}
            transition={{
              duration: 0.5,
              ease: [0.25, 0.1, 0.25, 1.0],
              type: "spring",
              stiffness: 300,
              damping: 30,
            }}
            className={`flex flex-col items-center gap-4 rounded-xl p-6 backdrop-blur-md transition-all duration-500 ${
              state.stage === "error"
                ? "bg-error/10 text-error"
                : state.stage === "completed"
                  ? "bg-success/10 text-success"
                  : "bg-primary/5"
            }`}
          >
            <div className="flex flex-col items-center gap-3">
              {state.stage === "error" ? (
                <WarningCircle className="text-error size-6 flex-shrink-0" />
              ) : state.stage === "completed" ? (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, type: "spring", stiffness: 500, damping: 25 }}
                >
                  <CheckCircle className="text-success size-6 flex-shrink-0" />
                </motion.div>
              ) : (
                <div className="relative">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  >
                    <Brain className="text-primary size-12 flex-shrink-0" />
                  </motion.div>
                  {/* Orbiting sparkles */}
                  <motion.div
                    className="absolute -top-2 -right-3"
                    animate={{ rotate: -360 }}
                    transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  >
                    <Sparkle className="text-warning size-5" />
                  </motion.div>
                  <motion.div
                    className="absolute -bottom-1 -left-3"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  >
                    <Lightning className="text-info size-5" />
                  </motion.div>
                </div>
              )}
              <p className="text-base font-medium mt-4">{statusMessage}</p>
              <div className="text-xs relative -top-2 text-muted-foreground">
                {t`Since we are running complex AI models, this may take a few minutes.`}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Background content area */}
      <div className="absolute inset-0 flex flex-col overflow-hidden">
        {state.currentText ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative h-full w-full overflow-hidden backdrop-blur-sm"
          >
            {/* Content */}
            <div className="h-full overflow-hidden">
              <motion.div
                className={`prose prose-base dark:prose-invert max-w-none p-8 transition-all duration-500 ${
                  state.isStreaming ? "opacity-40 blur-[1px]" : "opacity-70 blur-[0.5px]"
                }`}
                animate={{
                  filter: state.isStreaming
                    ? "blur(1px) brightness(0.8)"
                    : "blur(0.5px) brightness(0.9)",
                }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                <Markdown rehypePlugins={[rehypeRaw]}>{state.currentText}</Markdown>

                {/* Enhanced animated cursor for streaming */}
                {state.isStreaming && (
                  <motion.span
                    className="bg-primary ml-1 inline-block h-5 w-0.5"
                    animate={{
                      opacity: [0, 1, 0],
                      scale: [1, 1.2, 1],
                    }}
                    transition={{
                      duration: 1.2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                )}
              </motion.div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="bg-background/80 flex h-full w-full items-center justify-center"
          ></motion.div>
        )}
      </div>
    </div>
  );
};
